import { useCallback, useEffect, useState } from "react";

export const MONITOR_VISUAL_HIGHLIGHT_STORAGE_PREFIX =
  "cf-feed-monitor-visual-highlight:v1:";

export type MonitorVisualHighlightState = {
  productIds: string[];
  variantSessionIds: string[];
};

const EMPTY_HIGHLIGHT_STATE: MonitorVisualHighlightState = {
  productIds: [],
  variantSessionIds: [],
};

export const MONITOR_VISUAL_HIGHLIGHT_MAX_PRODUCTS = 3;

export function monitorVisualHighlightStorageKey(feedId: string): string {
  return `${MONITOR_VISUAL_HIGHLIGHT_STORAGE_PREFIX}${feedId}`;
}

export function readMonitorVisualHighlight(
  feedId: string,
): MonitorVisualHighlightState {
  try {
    const raw = localStorage.getItem(monitorVisualHighlightStorageKey(feedId));
    if (!raw) return EMPTY_HIGHLIGHT_STATE;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return EMPTY_HIGHLIGHT_STATE;

    const record = parsed as Record<string, unknown>;
    const productIds = Array.isArray(record.productIds)
      ? record.productIds.filter(
          (value): value is string => typeof value === "string",
        )
      : [];
    const variantSessionIds = Array.isArray(record.variantSessionIds)
      ? record.variantSessionIds.filter(
          (value): value is string => typeof value === "string",
        )
      : [];

    return {
      productIds: productIds.slice(-MONITOR_VISUAL_HIGHLIGHT_MAX_PRODUCTS),
      variantSessionIds,
    };
  } catch {
    return EMPTY_HIGHLIGHT_STATE;
  }
}

export function writeMonitorVisualHighlight(
  feedId: string,
  state: MonitorVisualHighlightState,
): void {
  try {
    localStorage.setItem(
      monitorVisualHighlightStorageKey(feedId),
      JSON.stringify({
        ...state,
        productIds: state.productIds.slice(
          -MONITOR_VISUAL_HIGHLIGHT_MAX_PRODUCTS,
        ),
      }),
    );
  } catch {
    // private browsing / storage disabled
  }
}

export function isProductVisuallyHighlighted(
  state: MonitorVisualHighlightState,
  productId: string,
): boolean {
  return state.productIds.includes(productId);
}

export function isVariantVisuallyHighlighted(
  state: MonitorVisualHighlightState,
  sessionProductId: string,
): boolean {
  return state.variantSessionIds.includes(sessionProductId);
}

export function toggleProductVisualHighlight(
  state: MonitorVisualHighlightState,
  productId: string,
): MonitorVisualHighlightState {
  if (isProductVisuallyHighlighted(state, productId)) {
    return {
      ...state,
      productIds: state.productIds.filter((id) => id !== productId),
    };
  }
  return {
    ...state,
    productIds: [...state.productIds, productId].slice(
      -MONITOR_VISUAL_HIGHLIGHT_MAX_PRODUCTS,
    ),
  };
}

export function toggleVariantVisualHighlight(
  state: MonitorVisualHighlightState,
  sessionProductId: string,
): MonitorVisualHighlightState {
  if (isVariantVisuallyHighlighted(state, sessionProductId)) {
    return {
      ...state,
      variantSessionIds: state.variantSessionIds.filter(
        (id) => id !== sessionProductId,
      ),
    };
  }
  return {
    ...state,
    variantSessionIds: [...state.variantSessionIds, sessionProductId],
  };
}

/** sync highlight เน้น visual บนแท็บ monitor / pop-out (localStorage + storage event) */
export function useFeedProductMonitorVisualHighlight(feedId: string) {
  const [highlightState, setHighlightState] = useState(() =>
    readMonitorVisualHighlight(feedId),
  );

  useEffect(() => {
    setHighlightState(readMonitorVisualHighlight(feedId));
  }, [feedId]);

  useEffect(() => {
    const storageKey = monitorVisualHighlightStorageKey(feedId);
    const onStorage = (event: StorageEvent) => {
      if (event.key !== storageKey) return;
      setHighlightState(readMonitorVisualHighlight(feedId));
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [feedId]);

  const toggleProductHighlight = useCallback(
    (productId: string) => {
      setHighlightState((current) => {
        const next = toggleProductVisualHighlight(current, productId);
        writeMonitorVisualHighlight(feedId, next);
        return next;
      });
    },
    [feedId],
  );

  const toggleVariantHighlight = useCallback(
    (sessionProductId: string) => {
      setHighlightState((current) => {
        const next = toggleVariantVisualHighlight(current, sessionProductId);
        writeMonitorVisualHighlight(feedId, next);
        return next;
      });
    },
    [feedId],
  );

  const isProductHighlighted = useCallback(
    (productId: string) =>
      isProductVisuallyHighlighted(highlightState, productId),
    [highlightState],
  );

  const isVariantHighlighted = useCallback(
    (sessionProductId: string) =>
      isVariantVisuallyHighlighted(highlightState, sessionProductId),
    [highlightState],
  );

  return {
    highlightState,
    isProductHighlighted,
    isVariantHighlighted,
    toggleProductHighlight,
    toggleVariantHighlight,
  };
}
